/**
 * O painel da direita da Operação: o que cada IA disse, e a conferência do operador.
 *
 * É a metade "APIs" do mockup da Gisele — OCR, contagem de eixos, anonimização, isenção,
 * divergência, status CDT — e embaixo o formulário onde o operador **corrige à mão** placa,
 * categoria e eixos, e decide: aprovar, sinalizar para revisão, escalar para o backoffice.
 *
 * Três regras organizam o arquivo:
 *
 * **O que a IA leu fica à vista ao lado do que o operador digita.** O campo nasce com a
 * leitura, e cada campo diz "IA: …" embaixo. Corrigir não apaga a leitura — nem aqui, nem
 * no banco (`decisoes_de_validacao` guarda o antes e o depois).
 *
 * **Os botões vêm do servidor** (`acoes_de_validacao`, calculado pelo domínio para o perfil
 * de quem pediu). A tela não tem cópia da tabela de transições: o operador não vê "Devolver"
 * num trânsito em revisão, e o supervisor vê — porque o domínio diz, não porque um `if`
 * aqui repete o que ele diz (§4, regra 4).
 *
 * **Corrigir exige motivo; aprovar como está, não.** Mesma regra do domínio, só que dita
 * antes do clique: o botão muda para "Aprovar com correção" e o motivo passa a ser pedido
 * no rótulo do campo. Preencher o que a IA deixou vazio não é correção.
 *
 * **A fraude pede o motivo da lista e a vista que o mostra** (`OB-01`, ADR-066): o botão abre
 * um formulário com as duas escolhas, e a confirmação da supervisão nasce com as da suspeita.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";

import { ErroDaApi, api } from "../api/cliente";
import {
  APRESENTACAO_DA_VALIDACAO,
  APRESENTACAO_DO_STATUS,
  ehDemonstracao,
  imagemDaAnalise,
  veioDeUpload,
} from "../api/tipos";
import type {
  AcaoDeValidacao,
  DetalheDoTransito,
  MotivoDeFraude,
  NotaGravada,
  ResultadoDaCapacidade,
  ResultadoDaValidacao,
} from "../api/tipos";
import { Badge, Botao, Cartao, Estado, Painel } from "../componentes/base";
import { TINTA } from "../componentes/tinta";
import type { Significado } from "../componentes/tinta";
import { Icone } from "../componentes/icones";
import { contagem, hora, percentual, reais } from "../formato";
import { useAgora } from "../ganchos/useAgora";
import { grupoDaClasse, resumoDaCapacidade } from "../leituras";
import { motivoDaAdocao, placasCandidatas } from "./candidatas";
import {
  FONTE_DOS_MOTIVOS,
  MOTIVOS_DE_FRAUDE,
  linhaDaFraude,
  suspeitaAberta,
  vistasDoTransito,
} from "./fraude";
import type { VistaDaFraude } from "./fraude";
import type { PlacaCandidata } from "./candidatas";
import { CadastroNaConferencia } from "./CadastroDoVeiculo";
import { votoDaClasse } from "./classe";
import {
  falhaDoServico,
  fraseDoProblema,
  problemasDasIas,
} from "./diagnosticoDasIas";
import { motivoDosEixos } from "./eixos";
import { origemDaSugestao } from "./sugestao";
import { itensDaIa } from "./trilha";
import type { ItemDaTrilha } from "./trilha";
import { agoraDoInquilino } from "../relogio";

/** As categorias da estrutura padrão (`ESTRUTURA_PADRAO` no domínio): as de quando o detalhe
 * ainda não diz as da concessão. A concessão com outra estrutura tarifária (a de MS, `SC-12`)
 * as recebe no detalhe (`categorias_da_concessao`), e o formulário lista essas. */
const CATEGORIAS: { valor: number; rotulo: string }[] = [
  { valor: 1, rotulo: "1 — automóvel, caminhonete" },
  { valor: 2, rotulo: "2 — caminhão leve, ônibus 2 eixos" },
  { valor: 3, rotulo: "3 — automóvel com semirreboque" },
  { valor: 4, rotulo: "4 — caminhão ou ônibus 3 eixos" },
  { valor: 5, rotulo: "5 — automóvel com reboque" },
  { valor: 6, rotulo: "6 — caminhão 4 eixos" },
  { valor: 7, rotulo: "7 — caminhão 5 eixos ou mais" },
  { valor: 8, rotulo: "8 — especial (moto)" },
];
const EIXOS = [2, 3, 4, 5, 6, 7, 8, 9];

const ROTULO_DA_ACAO: Record<AcaoDeValidacao, string> = {
  APROVAR: "Aprovar",
  SINALIZAR_REVISAO: "Indicar revisão",
  ESCALAR_BACKOFFICE: "Escalar ao backoffice",
  DEVOLVER: "Devolver à fila",
  SUSPEITAR_FRAUDE: "Suspeita de fraude",
  CONFIRMAR_FRAUDE: "Confirmar fraude",
};

/** No botão, o rótulo cabe numa linha; o nome inteiro fica no `aria-label` e na trilha. */
const ROTULO_CURTO: Record<AcaoDeValidacao, string> = {
  APROVAR: "Aprovar",
  SINALIZAR_REVISAO: "Indicar revisão",
  ESCALAR_BACKOFFICE: "Backoffice",
  DEVOLVER: "Devolver",
  SUSPEITAR_FRAUDE: "Fraude?",
  CONFIRMAR_FRAUDE: "Confirmar fraude",
};

type AcaoDeFraude = "SUSPEITAR_FRAUDE" | "CONFIRMAR_FRAUDE";

function ehFraude(acao: AcaoDeValidacao): acao is AcaoDeFraude {
  return acao === "SUSPEITAR_FRAUDE" || acao === "CONFIRMAR_FRAUDE";
}

/** O formulário da fraude aberto: a ação e as duas escolhas que ela pede. */
interface Fraude {
  acao: AcaoDeFraude;
  motivo: MotivoDeFraude | "";
  vista: string;
}

interface Resultado {
  texto: string;
  cor: "verde" | "vermelho" | "amarelo";
}

export function PainelDeValidacao({
  detalhe,
  analise,
  analiseDasVistas = {},
  carregando,
  erro,
  posicao,
  total,
  aoNavegar,
  aoMudar,
}: {
  detalhe: DetalheDoTransito | null;
  analise: ResultadoDaCapacidade[];
  /** O que as IAs disseram sobre cada vista (pela imagem), para o diagnóstico (03/11). */
  analiseDasVistas?: Record<string, ResultadoDaCapacidade[]>;
  carregando: boolean;
  erro: ErroDaApi | null;
  posicao: number;
  total: number;
  aoNavegar: (passo: -1 | 1) => void;
  /** Depois de uma decisão: recarregar fila, régua e detalhe. `saiu` diz se o trânsito
   *  deixou o recorte — aí a seleção anda para o próximo, que é o ritmo da conferência. */
  aoMudar: (
    saiu: boolean,
    aviso?: string,
    decisao?: AcaoDeValidacao,
  ) => Promise<void>;
}) {
  if (erro) {
    return (
      <Painel titulo="APIs e conferência">
        <Estado
          tipo={erro.semLicenca ? "sem-licenca" : "erro"}
          titulo={
            erro.semLicenca
              ? "Esta organização não licenciou o módulo TollVision."
              : "Não foi possível carregar o trânsito."
          }
          detalhe={erro.message}
        />
      </Painel>
    );
  }
  if (!detalhe) {
    return (
      <Painel titulo="APIs e conferência">
        <Estado
          tipo={carregando ? "carregando" : "vazio"}
          titulo={
            carregando
              ? "Carregando o trânsito…"
              : "Selecione um trânsito na fila."
          }
        />
      </Painel>
    );
  }
  // `key`: trocar de trânsito zera o formulário. Sem isto, a placa digitada para um
  // veículo ficaria no campo quando o próximo abrisse — e seria aprovada nele.
  return (
    <Conferencia
      key={detalhe.id}
      detalhe={detalhe}
      analise={analise}
      analiseDasVistas={analiseDasVistas}
      posicao={posicao}
      total={total}
      aoNavegar={aoNavegar}
      aoMudar={aoMudar}
    />
  );
}

function Conferencia({
  detalhe,
  analise,
  analiseDasVistas,
  posicao,
  total,
  aoNavegar,
  aoMudar,
}: {
  detalhe: DetalheDoTransito;
  analise: ResultadoDaCapacidade[];
  analiseDasVistas: Record<string, ResultadoDaCapacidade[]>;
  posicao: number;
  total: number;
  aoNavegar: (passo: -1 | 1) => void;
  aoMudar: (
    saiu: boolean,
    aviso?: string,
    decisao?: AcaoDeValidacao,
  ) => Promise<void>;
}) {
  const leitura = useMemo(
    () => leituraDaIa(detalhe, analise, analiseDasVistas),
    [detalhe, analise, analiseDasVistas],
  );
  const quantosNaTrilha = useMemo(
    () => itensDaTrilha(detalhe).length,
    [detalhe],
  );
  const sugestao = useMemo(
    () =>
      origemDaSugestao({
        categoria: leitura.categoria,
        grupo: detalhe.silhueta,
        eixos: leitura.eixos,
        origemDosEixos:
          leitura.eixos == null
            ? null
            : motivoDosEixos(detalhe.imagens_de_analise ?? [], leitura.eixos),
        suspensos: Math.max(
          0,
          ...(detalhe.imagens_de_analise ?? [])
            .filter((v) => v.eixos === leitura.eixos)
            .map((v) => v.eixos_suspensos ?? 0),
        ),
      }),
    [leitura, detalhe.silhueta, detalhe.imagens_de_analise],
  );
  // O cadastro preenche a categoria que a IA não alcançou (R-02, tarefa 4): o caminhão de
  // frente, de eixos não contados, chega com a categoria que ele tem no cadastro — dita como tal.
  const doCadastro =
    detalhe.categoria_no_cadastro?.situacao === "SO_CADASTRO"
      ? detalhe.categoria_no_cadastro
      : null;
  const categoriaSugerida = leitura.categoria ?? doCadastro?.categoria ?? null;
  const textoDaSugestao =
    leitura.categoria == null && doCadastro ? doCadastro.explicacao : sugestao;
  const [placa, definirPlaca] = useState(detalhe.placa ?? leitura.placa ?? "");
  const [categoria, definirCategoria] = useState<string>(
    String(detalhe.categoria ?? categoriaSugerida ?? ""),
  );
  const [eixos, definirEixos] = useState<string>(
    String(detalhe.eixos ?? leitura.eixos ?? ""),
  );
  const [motivo, definirMotivo] = useState("");
  const candidatas = useMemo(() => placasCandidatas(detalhe), [detalhe]);
  const [nota, definirNota] = useState<string | null>(null);
  const [emCurso, definirEmCurso] = useState<string | null>(null);
  const [resultado, definirResultado] = useState<Resultado | null>(null);
  /** A ação que o operador tentou sem motivo — o campo do motivo diz para quê. */
  const [pediuMotivo, definirPediuMotivo] = useState<AcaoDeValidacao | null>(
    null,
  );
  const campoDoMotivo = useRef<HTMLTextAreaElement>(null);
  const campoDaPlaca = useRef<HTMLInputElement>(null);
  const campoDaCategoria = useRef<HTMLSelectElement>(null);
  /** Tentou aprovar sem categoria: o campo diz o que falta (review de 21/09, 18h11). */
  const [pediuCategoria, definirPediuCategoria] = useState(false);
  const [fraude, definirFraude] = useState<Fraude | null>(null);
  const vistas = useMemo(() => vistasDoTransito(detalhe), [detalhe]);

  // A decisão recarrega o detalhe com a situação nova; o formulário acompanha o que o banco
  // gravou (placa normalizada, por exemplo), e não o que foi digitado.
  useEffect(() => {
    definirPlaca(detalhe.placa ?? leitura.placa ?? "");
    definirCategoria(String(detalhe.categoria ?? categoriaSugerida ?? ""));
    definirEixos(String(detalhe.eixos ?? leitura.eixos ?? ""));
  }, [
    detalhe.placa,
    detalhe.categoria,
    detalhe.eixos,
    leitura,
    categoriaSugerida,
  ]);

  const acoes = detalhe.acoes_de_validacao;
  const editavel = acoes.includes("APROVAR");
  const placaLimpa = placa
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, "");
  // Mesma regra do servidor: só é correção mudar o que **já estava** gravado.
  const corrigiu =
    (detalhe.placa !== null && placaLimpa !== detalhe.placa) ||
    (detalhe.categoria !== null && categoria !== String(detalhe.categoria)) ||
    (detalhe.eixos !== null && eixos !== String(detalhe.eixos));
  const motivoOk = motivo.trim().length >= 5;

  /**
   * Toda ação que precisa de motivo e não tem **não decide e não some**: leva o cursor ao
   * motivo e diz no rótulo para quê. Botão desabilitado sem explicação foi o que os testes
   * de 21/09 leram como "não tem como indicar revisão".
   */
  function tentar(acao: AcaoDeValidacao) {
    if (emCurso !== null) return;
    // A fraude não decide no clique: abre as duas escolhas que ela pede. A confirmação
    // nasce com as da suspeita, e a supervisão confere.
    if (ehFraude(acao)) {
      const suspeita =
        acao === "CONFIRMAR_FRAUDE" ? suspeitaAberta(detalhe) : null;
      definirResultado(null);
      // A vista da suspeita só vem escolhida se ainda está na lista: a imagem pode ter
      // ficado indisponível depois, e escolhê-la seria um formulário "completo" que o
      // servidor recusa.
      const daSuspeita = vistas.find((v) => v.id === suspeita?.evidencia_id);
      definirFraude({
        acao,
        motivo: suspeita?.motivo_de_fraude ?? "",
        vista: daSuspeita?.id ?? (vistas.length === 1 ? vistas[0]!.id : ""),
      });
      return;
    }
    // Sem categoria não há tarifa, e o servidor recusa a aprovação. O botão não pode
    // "não fazer nada" (Gisele, 21/09: ônibus sem eixos contados, sem categoria sugerida, e o
    // campo abaixo da dobra): leva ao campo, rola até ele e diz o que falta.
    if (acao === "APROVAR" && !categoria) {
      definirPediuCategoria(true);
      definirResultado({
        texto:
          "Escolha a categoria (1 a 7, ou 8 para Especial) para aprovar — é ela que dá a tarifa.",
        cor: "amarelo",
      });
      campoDaCategoria.current?.scrollIntoView?.({
        block: "center",
        behavior: "smooth",
      });
      campoDaCategoria.current?.focus();
      return;
    }
    const exige = acao === "APROVAR" ? corrigiu : true;
    if (exige && !motivoOk) {
      definirPediuMotivo(acao);
      campoDoMotivo.current?.focus();
      return;
    }
    void decidir(acao);
  }

  function corrigirPlaca() {
    campoDaPlaca.current?.focus();
    campoDaPlaca.current?.select();
  }

  /**
   * "Adotar sugestão" (C-11, CA-03): um clique põe a placa que aquela câmera leu no campo e,
   * se isso for correção, escreve o motivo — que o operador pode mudar. Não aprova: quem
   * decide continua sendo o botão de aprovar.
   */
  function adotar(c: PlacaCandidata) {
    definirPlaca(c.placa);
    definirResultado(null);
    const eCorrecao = detalhe.placa !== null && c.placa !== detalhe.placa;
    if (eCorrecao && !motivo.trim()) definirMotivo(motivoDaAdocao(c));
    if (!eCorrecao && motivo.startsWith("Adotada a leitura")) definirMotivo("");
  }

  async function decidir(acao: AcaoDeValidacao) {
    definirEmCurso(acao);
    definirResultado(null);
    try {
      const corpo: Record<string, unknown> = { acao };
      if (motivo.trim()) corpo.motivo = motivo.trim();
      if (ehFraude(acao) && fraude) {
        corpo.motivo_de_fraude = fraude.motivo || null;
        corpo.evidencia = fraude.vista || null;
        // A confirmação leva a categoria do campo acima quando ela mudou: é a tarifa devida,
        // que o poder concedente devolve inteira. A que já estava, o servidor mantém.
        if (
          acao === "CONFIRMAR_FRAUDE" &&
          categoria &&
          Number(categoria) !== detalhe.categoria
        )
          corpo.categoria = Number(categoria);
        // Os eixos também, pela mesma regra: o servidor só os usa onde a categoria vale por
        // eles — a especial de MS (OB-11) —, e nas outras ficam os do trânsito.
        if (
          acao === "CONFIRMAR_FRAUDE" &&
          eixos &&
          Number(eixos) !== detalhe.eixos
        )
          corpo.eixos = Number(eixos);
      }
      if (acao === "APROVAR") {
        corpo.placa = placaLimpa || null;
        corpo.categoria = categoria ? Number(categoria) : null;
        corpo.eixos = eixos ? Number(eixos) : null;
      }
      const r = await api<ResultadoDaValidacao>(
        `/tollvision/transitos/${detalhe.id}/validacao`,
        { method: "POST", corpo },
      );
      // O aviso sobe para a tela: a decisão tira o trânsito do recorte, a seleção anda para
      // o próximo e este painel é remontado — uma mensagem guardada aqui morreria junto.
      definirMotivo("");
      definirFraude(null);
      await aoMudar(
        true,
        acao === "APROVAR"
          ? `${r.placa ?? "Trânsito"} aprovado — categoria ${r.categoria ?? "—"}, ${reais(r.valor_centavos)}. Já está na fila da SENATRAN.`
          : `${detalhe.placa ?? "Trânsito"}: ${ROTULO_DA_ACAO[acao].toLowerCase()} — agora ${APRESENTACAO_DA_VALIDACAO[r.validacao].rotulo.toLowerCase()}.`,
        acao,
      );
    } catch (falha) {
      definirResultado({
        texto:
          falha instanceof ErroDaApi
            ? falha.message
            : "Não foi possível registrar.",
        cor:
          falha instanceof ErroDaApi && falha.status === 403
            ? "amarelo"
            : "vermelho",
      });
    } finally {
      definirEmCurso(null);
    }
  }

  async function anotar() {
    if (!nota || nota.trim().length < 2) return;
    definirEmCurso("nota");
    try {
      await api<NotaGravada>(`/tollvision/transitos/${detalhe.id}/notas`, {
        method: "POST",
        corpo: { texto: nota.trim() },
      });
      definirNota(null);
      definirResultado({ texto: "Nota registrada na trilha.", cor: "verde" });
      await aoMudar(false);
    } catch (falha) {
      definirResultado({
        texto:
          falha instanceof ErroDaApi
            ? falha.message
            : "Não foi possível gravar a nota.",
        cor: "vermelho",
      });
    } finally {
      definirEmCurso(null);
    }
  }

  const situacao = APRESENTACAO_DA_VALIDACAO[detalhe.validacao];
  const prazo24 = detalhe.prazos.find((p) => p.tipo === "ENVIO_24H");

  /**
   * Os atalhos do posto de conferência: A aprova, R indica revisão, B escala para o
   * backoffice, P vai à placa, N abre a nota. **Nunca dentro de um campo** — digitar
   * "BRA2E19" na placa não pode escalar o trânsito para o backoffice no meio da palavra.
   */
  useEffect(() => {
    function aoTeclar(e: KeyboardEvent) {
      if (e.ctrlKey || e.metaKey || e.altKey || digitando(e.target)) return;
      const tecla = e.key.toLowerCase();
      const acao = (
        {
          a: "APROVAR",
          r: "SINALIZAR_REVISAO",
          b: "ESCALAR_BACKOFFICE",
        } as const
      )[tecla as "a" | "r" | "b"];
      if (acao && acoes.includes(acao)) {
        e.preventDefault();
        tentar(acao);
      } else if (tecla === "p" && editavel) {
        e.preventDefault();
        corrigirPlaca();
      } else if (tecla === "n") {
        e.preventDefault();
        definirNota((n) => (n === null ? "" : n));
      }
    }
    window.addEventListener("keydown", aoTeclar);
    return () => window.removeEventListener("keydown", aoTeclar);
  });

  const secundarias = (
    [
      "SINALIZAR_REVISAO",
      "ESCALAR_BACKOFFICE",
      "DEVOLVER",
      "SUSPEITAR_FRAUDE",
      "CONFIRMAR_FRAUDE",
    ] as const
  ).filter((a) => acoes.includes(a));

  return (
    <Painel
      titulo="Conferência"
      acoes={
        <Navegacao posicao={posicao} total={total} aoNavegar={aoNavegar} />
      }
      rodape={
        <div className="grid w-full gap-2">
          {acoes.includes("APROVAR") && (
            <Botao
              papel="aprovar"
              disabled={emCurso !== null}
              onClick={() => tentar("APROVAR")}
            >
              <Icone.Certo />
              {emCurso === "APROVAR"
                ? "Aprovando…"
                : !categoria
                  ? "Aprovar — escolha a categoria"
                  : corrigiu
                    ? "Aprovar com correção"
                    : "Aprovar"}
              <Tecla>A</Tecla>
            </Botao>
          )}
          <div className="grid grid-cols-2 gap-2">
            {editavel && (
              <Botao disabled={emCurso !== null} onClick={corrigirPlaca}>
                <Icone.Busca />
                Corrigir placa
                <Tecla>P</Tecla>
              </Botao>
            )}
            {secundarias.map((a) => (
              <Botao
                key={a}
                papel={
                  a === "ESCALAR_BACKOFFICE" || a === "CONFIRMAR_FRAUDE"
                    ? "perigo"
                    : a === "SINALIZAR_REVISAO" || a === "SUSPEITAR_FRAUDE"
                      ? "atencao"
                      : "secundaria"
                }
                disabled={emCurso !== null}
                aria-label={ROTULO_DA_ACAO[a]}
                aria-expanded={ehFraude(a) ? fraude?.acao === a : undefined}
                title={
                  ehFraude(a)
                    ? `${ROTULO_DA_ACAO[a]} — pede o motivo da lista e a vista que o mostra`
                    : `${ROTULO_DA_ACAO[a]}${motivoOk ? "" : " — pede motivo"}`
                }
                onClick={() => tentar(a)}
              >
                {a === "DEVOLVER" ? <Icone.Recarga /> : <Icone.Alerta />}
                {emCurso === a ? "Registrando…" : ROTULO_CURTO[a]}
                {TECLA_DA_ACAO[a] && <Tecla>{TECLA_DA_ACAO[a]}</Tecla>}
              </Botao>
            ))}
            <Botao
              disabled={emCurso !== null}
              onClick={() => definirNota(nota === null ? "" : null)}
            >
              <Icone.Nota />
              Adicionar nota
              <Tecla>N</Tecla>
            </Botao>
          </div>
          {fraude && (
            <FormularioDaFraude
              fraude={fraude}
              vistas={vistas}
              categoria={categoria}
              emCurso={emCurso !== null}
              aoMudar={definirFraude}
              aoRegistrar={() => void decidir(fraude.acao)}
              aoCancelar={() => definirFraude(null)}
            />
          )}
          {nota !== null && (
            <div className="grid gap-1.5">
              <textarea
                value={nota}
                onChange={(e) => definirNota(e.target.value)}
                rows={2}
                maxLength={1000}
                aria-label="Nota"
                autoFocus
                placeholder="A nota entra na trilha do trânsito, com o seu nome e a hora."
                className="rounded-botao border border-linha bg-painel-2 p-2 text-tinta"
              />
              <Botao
                disabled={nota.trim().length < 2 || emCurso !== null}
                onClick={() => void anotar()}
              >
                Gravar nota
              </Botao>
            </div>
          )}
          {resultado ? (
            <p
              role="status"
              className={`text-[12px] font-semibold ${COR_DO_RESULTADO[resultado.cor]}`}
            >
              {resultado.texto}
            </p>
          ) : (
            <p className="text-[10.5px] text-tinta-3">{explicacao(detalhe)}</p>
          )}
        </div>
      }
    >
      <div className="flex flex-wrap items-center gap-1.5">
        <Badge cor={situacao.cor}>{situacao.rotulo}</Badge>
        {detalhe.status !== "CAPTURADO" && (
          <Badge cor={APRESENTACAO_DO_STATUS[detalhe.status].cor}>
            {APRESENTACAO_DO_STATUS[detalhe.status].rotulo}
          </Badge>
        )}
        {veioDeUpload(detalhe) ? (
          <Badge cor="azul">upload</Badge>
        ) : (
          ehDemonstracao(detalhe) && <Badge cor="cinza">simulado</Badge>
        )}
        <span className="ml-auto">
          {prazo24 ? (
            <RelogioDoPrazo venceEm={prazo24.vence_em} />
          ) : (
            <span className="mono text-[11px] text-tinta-3">prazo 24 h: —</span>
          )}
        </span>
      </div>

      <LeiturasDaIa leitura={leitura} />

      {!editavel && (
        <p className="text-[11px] text-tinta-3">
          {detalhe.validacao === "APROVADO"
            ? `Aprovado${detalhe.validado_por_nome ? ` por ${detalhe.validado_por_nome}` : ""}${detalhe.validado_em ? ` às ${hora(detalhe.validado_em)}` : ""}. Para corrigir, devolva à fila antes do envio.`
            : "Os dados só mudam junto com a aprovação — e aprovar este trânsito não está ao seu alcance agora."}
        </p>
      )}

      <Campo
        rotulo="Placa"
        ia={leitura.placa}
        mudou={placaLimpa !== (leitura.placa ?? "")}
      >
        <input
          ref={campoDaPlaca}
          value={placa}
          disabled={!editavel}
          onChange={(e) => definirPlaca(e.target.value.toUpperCase())}
          maxLength={8}
          aria-label="Placa"
          placeholder="ABC1D23"
          className="mono w-full rounded-botao border border-linha bg-painel px-3 py-1.5 text-[24px] leading-none font-bold tracking-[0.12em] text-tinta focus:border-azul disabled:opacity-60"
        />
      </Campo>
      <div className="grid grid-cols-[1fr_110px] gap-2">
        <Campo
          rotulo="Categoria"
          ia={leitura.categoria == null ? null : String(leitura.categoria)}
          mudou={categoria !== String(leitura.categoria ?? "")}
          alerta={pediuCategoria && !categoria ? "escolha para aprovar" : null}
        >
          <select
            ref={campoDaCategoria}
            value={categoria}
            disabled={!editavel}
            onChange={(e) => {
              definirCategoria(e.target.value);
              if (e.target.value) definirResultado(null);
            }}
            aria-label="Categoria"
            className={`w-full rounded-botao border bg-painel px-2 py-1.5 text-tinta disabled:opacity-60 ${
              pediuCategoria && !categoria
                ? "border-amarelo ring-2 ring-amarelo/40"
                : "border-linha"
            }`}
          >
            <option value="">—</option>
            {(detalhe.categorias_da_concessao?.length
              ? detalhe.categorias_da_concessao
              : CATEGORIAS
            ).map((c) => (
              <option key={c.valor} value={c.valor}>
                {c.rotulo}
              </option>
            ))}
          </select>
        </Campo>
        <Campo
          rotulo="Eixos (opcional)"
          ia={leitura.eixos == null ? null : String(leitura.eixos)}
          mudou={eixos !== String(leitura.eixos ?? "")}
        >
          <select
            value={eixos}
            disabled={!editavel}
            onChange={(e) => definirEixos(e.target.value)}
            aria-label="Eixos"
            className="w-full rounded-botao border border-linha bg-painel px-2 py-1.5 text-tinta disabled:opacity-60"
          >
            <option value="">—</option>
            {(detalhe.eixos_da_concessao?.length
              ? detalhe.eixos_da_concessao
              : EIXOS
            ).map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
        </Campo>
      </div>
      <CadastroNaConferencia
        detalhe={detalhe}
        categoriaEscolhida={categoria ? Number(categoria) : null}
      />
      {editavel && candidatas.length > 1 && (
        <AdotarSugestao
          candidatas={candidatas}
          atual={placaLimpa}
          aoAdotar={adotar}
        />
      )}
      {textoDaSugestao && (
        <p className="-mt-1 text-[11px] text-tinta-3">{textoDaSugestao}</p>
      )}
      <label className="grid gap-1 text-[11px] text-tinta-3">
        <span>
          Motivo{" "}
          {corrigiu ? (
            <b className="text-amarelo">
              — obrigatório: você corrigiu o que a IA leu
            </b>
          ) : pediuMotivo && !motivoOk ? (
            <b className="text-amarelo">
              — diga o motivo para {ROTULO_DA_ACAO[pediuMotivo].toLowerCase()}
            </b>
          ) : (
            "— para corrigir, indicar revisão ou escalar"
          )}
        </span>
        <textarea
          ref={campoDoMotivo}
          value={motivo}
          onChange={(e) => definirMotivo(e.target.value)}
          rows={2}
          maxLength={500}
          aria-label="Motivo"
          placeholder="Ex.: o 8 da placa era um B, conferido na lateral."
          className={`rounded-botao border bg-painel p-2 text-tinta focus:border-azul ${
            (corrigiu || pediuMotivo) && !motivoOk
              ? "border-amarelo-borda"
              : "border-linha"
          }`}
        />
      </label>

      <details className="rounded-cartao border border-linha bg-painel-2 p-2">
        <summary className="cursor-pointer text-titulo-cartao font-bold tracking-[0.07em] text-tinta-3 uppercase">
          Trilha da conferência
          {quantosNaTrilha > 0 && ` (${quantosNaTrilha})`}
        </summary>
        <div className="mt-2">
          <TrilhaDaValidacao detalhe={detalhe} />
        </div>
      </details>
    </Painel>
  );
}

/**
 * As leituras das IAs em seis blocos, cada um com **uma** cor de significado.
 *
 * Feedback de 21/09: "está tudo num tom só". Então cada bloco diz uma coisa só, com a cor
 * do que ela significa — verde confere, âmbar pede olho, vermelho é problema, cinza é
 * "não se aplica". Isenção e classe aparecem sempre, inclusive quando a resposta é "não".
 */
function LeiturasDaIa({ leitura }: { leitura: LeituraDaIa }) {
  return (
    <section aria-label="Leituras da IA" className="grid gap-1.5">
      <h3 className="text-titulo-cartao font-bold tracking-[0.07em] text-tinta-3 uppercase">
        Leituras da IA
      </h3>
      <div className="grid grid-cols-3 gap-1.5">
        {leitura.blocos.map((b) => (
          <div
            key={b.rotulo}
            title={b.detalhe ?? undefined}
            className={`grid min-w-0 gap-0.5 rounded-cartao border px-2 py-1.5 ${TINTA_DO_BLOCO[b.cor]}`}
          >
            <span className="text-[9.5px] font-semibold tracking-[0.06em] text-tinta-3 uppercase">
              {b.rotulo}
            </span>
            <b
              className={`leading-tight ${b.mono ? "mono truncate text-[14px] tracking-[0.06em]" : "text-[12px] break-words uppercase"}`}
            >
              {b.porLetra ? (
                <PlacaPorLetra placa={b.valor} porLetra={b.porLetra} />
              ) : (
                b.valor
              )}
            </b>
            {b.detalhe && (
              <span className="line-clamp-2 text-[10px] leading-snug text-tinta-3">
                {b.detalhe}
              </span>
            )}
          </div>
        ))}
      </div>
      {leitura.diagnostico && leitura.diagnostico.length > 0 && (
        <div
          data-testid="diagnostico-das-ias"
          className="grid gap-0.5 rounded-cartao border border-amarelo-borda bg-amarelo-fundo px-2 py-1.5 text-[10.5px] leading-snug text-tinta-2"
        >
          <b className="text-[9.5px] font-semibold tracking-[0.06em] text-tinta-3 uppercase">
            Diagnóstico das IAs
          </b>
          {leitura.diagnostico.map((frase) => (
            <span key={frase}>{frase}</span>
          ))}
        </div>
      )}
    </section>
  );
}

/** O campo de digitação é do operador: atalho de uma letra não pode disparar ali. */
/**
 * As placas que as câmeras leram, cada uma a um clique do campo (C-11). Só aparece quando
 * há mais de uma — com uma leitura só, o campo já nasce com ela.
 */
function AdotarSugestao({
  candidatas,
  atual,
  aoAdotar,
}: {
  candidatas: PlacaCandidata[];
  atual: string;
  aoAdotar: (c: PlacaCandidata) => void;
}) {
  return (
    <div
      role="group"
      aria-label="Leituras das câmeras"
      className="-mt-1 grid gap-1 rounded-cartao border border-amarelo-borda bg-amarelo-fundo p-2"
    >
      <span className="text-[11px] text-amarelo">
        As câmeras leram placas diferentes. Adote a certa com um clique:
      </span>
      <div className="flex flex-wrap gap-1.5">
        {candidatas.map((c, i) => {
          const noCampo = c.placa === atual;
          return (
            <button
              key={c.placa}
              type="button"
              disabled={noCampo}
              aria-pressed={noCampo}
              aria-label={`Adotar ${c.placa}, lida por ${c.fontes.join(" e ")}`}
              onClick={() => aoAdotar(c)}
              className="grid min-h-8 rounded-botao border border-linha bg-painel px-2 py-1 text-left text-tinta enabled:hover:border-azul disabled:border-verde disabled:opacity-100"
            >
              <span className="mono text-[13px] font-bold tracking-[0.08em]">
                {c.placa}
                {noCampo && " ✓"}
              </span>
              <small className="text-[10.5px] text-tinta-3">
                {c.fontes.join(" + ")}
                {c.confianca !== null && ` · ${c.confianca}%`}
                {i === 0 && " · melhor leitura"}
              </small>
            </button>
          );
        })}
      </div>
    </div>
  );
}

/**
 * As duas escolhas da fraude: o que o usuário fez com a placa (a lista da Portaria) e a vista
 * que mostra. O relatório mensal ao poder concedente leva a foto de cada passagem
 * fraudulenta — sem vista, não há o que registrar, e o botão diz isso.
 */
function FormularioDaFraude({
  fraude,
  vistas,
  categoria,
  emCurso,
  aoMudar,
  aoRegistrar,
  aoCancelar,
}: {
  fraude: Fraude;
  vistas: VistaDaFraude[];
  /** A do campo Categoria do painel: na confirmação, é a que dá a tarifa devida. */
  categoria: string;
  emCurso: boolean;
  aoMudar: (f: Fraude) => void;
  aoRegistrar: () => void;
  aoCancelar: () => void;
}) {
  const confirmar = fraude.acao === "CONFIRMAR_FRAUDE";
  const completo = Boolean(fraude.motivo && fraude.vista);
  return (
    <fieldset
      aria-label={ROTULO_DA_ACAO[fraude.acao]}
      className="grid gap-1.5 rounded-cartao border border-vermelho-borda bg-vermelho-fundo p-2"
    >
      <legend className="px-1 text-[11px] font-bold text-vermelho">
        {confirmar
          ? "Confirmar fraude: o poder concedente devolve a tarifa inteira"
          : "Suspeita de fraude: o supervisor confere e confirma"}
      </legend>
      <label className="grid gap-0.5 text-[11px] text-tinta-3">
        <span className="font-semibold text-tinta-2">
          O que o usuário fez com a placa
        </span>
        <select
          value={fraude.motivo}
          onChange={(e) =>
            aoMudar({
              ...fraude,
              motivo: e.target.value as MotivoDeFraude | "",
            })
          }
          aria-label="Motivo da fraude"
          className="w-full rounded-botao border border-linha bg-painel px-2 py-1.5 text-tinta"
        >
          <option value="">—</option>
          {MOTIVOS_DE_FRAUDE.map((m) => (
            <option key={m.valor} value={m.valor}>
              {m.rotulo}
            </option>
          ))}
        </select>
        <span className="text-[10px]">{FONTE_DOS_MOTIVOS}</span>
      </label>
      <label className="grid gap-0.5 text-[11px] text-tinta-3">
        <span className="font-semibold text-tinta-2">A vista que mostra</span>
        <select
          value={fraude.vista}
          disabled={vistas.length === 0}
          onChange={(e) => aoMudar({ ...fraude, vista: e.target.value })}
          aria-label="Vista da fraude"
          className="w-full rounded-botao border border-linha bg-painel px-2 py-1.5 text-tinta disabled:opacity-60"
        >
          <option value="">—</option>
          {vistas.map((v) => (
            <option key={v.id} value={v.id}>
              {v.rotulo}
            </option>
          ))}
        </select>
        {vistas.length === 0 && (
          <span className="text-[10px] text-amarelo">
            Este trânsito não tem imagem disponível: sem a foto, a fraude não
            tem prova.
          </span>
        )}
      </label>
      {confirmar && (
        <p className="text-[10.5px] text-tinta-3">
          {categoria
            ? `Tarifa devida pela categoria ${categoria} — mude no campo Categoria, acima.`
            : "Sem categoria, a passagem fica fora da compensação: escolha no campo Categoria, acima."}
        </p>
      )}
      <div className="grid grid-cols-2 gap-2">
        <Botao
          papel={confirmar ? "perigo" : "atencao"}
          disabled={!completo || emCurso}
          onClick={aoRegistrar}
        >
          <Icone.Alerta />
          {emCurso
            ? "Registrando…"
            : confirmar
              ? "Confirmar fraude"
              : "Registrar suspeita"}
        </Botao>
        <Botao disabled={emCurso} onClick={aoCancelar}>
          Cancelar
        </Botao>
      </div>
    </fieldset>
  );
}

function digitando(alvo: EventTarget | null): boolean {
  if (!(alvo instanceof HTMLElement)) return false;
  return (
    alvo.isContentEditable ||
    ["INPUT", "TEXTAREA", "SELECT"].includes(alvo.tagName)
  );
}

const TECLA_DA_ACAO: Partial<Record<AcaoDeValidacao, string>> = {
  SINALIZAR_REVISAO: "R",
  ESCALAR_BACKOFFICE: "B",
};

function Tecla({ children }: { children: ReactNode }) {
  return (
    <kbd
      aria-hidden
      className="mono ml-1 rounded-[4px] border border-current/40 px-1 text-[9.5px] leading-[14px] opacity-70"
    >
      {children}
    </kbd>
  );
}

function Campo({
  rotulo,
  ia,
  mudou,
  alerta,
  children,
}: {
  rotulo: string;
  ia: string | null;
  /** O operador pôs aqui outra coisa que não a leitura: o "IA: …" fica âmbar. */
  mudou: boolean;
  /** O que falta neste campo para a ação que o operador tentou — em âmbar, ao lado do rótulo. */
  alerta?: string | null;
  children: ReactNode;
}) {
  return (
    <label className="grid gap-0.5 text-[11px] text-tinta-3">
      <span className="font-semibold text-tinta-2">
        {rotulo}
        {alerta && <b className="ml-1 text-amarelo">— {alerta}</b>}
      </span>
      {children}
      <span
        className={`mono truncate text-[10px] ${mudou && ia ? "text-amarelo" : ""}`}
      >
        IA: {ia ?? "não leu"}
      </span>
    </label>
  );
}

/** Decisões e notas numa linha do tempo só — é assim que quem chega depois lê. */
/** A IA primeiro, depois as pessoas: é a ordem em que o trânsito aconteceu (tarefa 5). */
function itensDaTrilha(detalhe: DetalheDoTransito): ItemDaTrilha[] {
  const vistas = vistasDoTransito(detalhe);
  const pessoas: ItemDaTrilha[] = [
    ...detalhe.decisoes.map((d) => ({
      em: d.em,
      // A decisão automática (29/09): a placa abaixo do limiar da Configuração foi para
      // revisão sozinha. Quem decidiu foi o sistema, e a trilha diz isso — e diz por quê.
      quem: d.automatica ? "Sistema (automático)" : d.usuario_nome,
      titulo: `${ROTULO_DA_ACAO[d.acao as AcaoDeValidacao] ?? d.acao} — ${APRESENTACAO_DA_VALIDACAO[d.situacao_de].rotulo.toLowerCase()} → ${APRESENTACAO_DA_VALIDACAO[d.situacao_para].rotulo.toLowerCase()}`,
      mudancas: [
        mudou("placa", d.placa_antes, d.placa_depois),
        mudou("categoria", d.categoria_antes, d.categoria_depois),
        mudou("eixos", d.eixos_antes, d.eixos_depois),
        linhaDaFraude(d, vistas),
      ].filter(Boolean) as string[],
      texto: d.motivo,
      ia: d.automatica,
    })),
    ...detalhe.notas.map((n) => ({
      em: n.criado_em,
      quem: n.usuario_nome,
      titulo: "Nota",
      mudancas: [] as string[],
      texto: n.texto,
      ia: false,
    })),
  ].sort((a, b) => a.em.localeCompare(b.em));
  return [...itensDaIa(detalhe), ...pessoas];
}

export function TrilhaDaValidacao({ detalhe }: { detalhe: DetalheDoTransito }) {
  const itens = itensDaTrilha(detalhe);

  return (
    <Cartao titulo="Trilha da conferência" icone={<Icone.Relogio />}>
      {itens.length === 0 ? (
        <p className="text-[11px] text-tinta-3">
          Ninguém decidiu nada ainda. Cada decisão e cada nota entram aqui, com
          nome e hora — e não saem.
        </p>
      ) : (
        <ol className="grid gap-2">
          {itens.map((i, n) => (
            <li key={`${i.em}-${n}`} className="grid gap-0.5">
              <div className="flex flex-wrap items-center gap-1.5">
                <span className="mono text-[11px] text-tinta-3">
                  {hora(i.em)}
                </span>
                {i.ia && <Badge cor="azul">IA</Badge>}
                <b className="text-tinta-2">{i.titulo}</b>
              </div>
              {!i.ia && (
                <span className="text-[10.5px] text-tinta-3">{i.quem}</span>
              )}
              {i.mudancas.map((m) => (
                <span key={m} className="mono text-[10.5px] text-amarelo">
                  {m}
                </span>
              ))}
              {i.texto &&
                (i.ia ? (
                  <p className="text-[11px] text-tinta-3">{i.texto}</p>
                ) : (
                  <p className="text-[11px] text-tinta-2">“{i.texto}”</p>
                ))}
            </li>
          ))}
        </ol>
      )}
    </Cartao>
  );
}

function mudou(campo: string, antes: unknown, depois: unknown): string | null {
  if (depois == null || antes === depois) return null;
  return `${campo}: ${antes ?? "vazio"} → ${String(depois)}`;
}

function explicacao(detalhe: DetalheDoTransito): string {
  if (detalhe.status !== "CAPTURADO")
    return "Já enviado: a validação está encerrada. Correção agora é reclassificação na SENATRAN.";
  if (detalhe.validacao === "FRAUDE")
    return "Fraude confirmada pela supervisão: não vai à SENATRAN, e a passagem entra na compensação como inválida fraudulenta. Desfazer é da supervisão.";
  if (suspeitaAberta(detalhe) && detalhe.validacao === "EM_REVISAO")
    return "Suspeita de fraude: quem confirma, ou devolve à fila, é o supervisor.";
  if (detalhe.acoes_de_validacao.length === 0)
    return detalhe.validacao === "EM_REVISAO"
      ? "Sinalizado para revisão: quem decide é o supervisor."
      : detalhe.validacao === "BACKOFFICE"
        ? "No backoffice: quem decide é a equipe de backoffice."
        : "Nenhuma decisão está ao seu alcance neste trânsito.";
  return "Aprovado vai para a tela SENATRAN. Sem aprovação, o envio é recusado — pela tela e pelo banco.";
}

const COR_DO_RESULTADO = {
  verde: "text-verde",
  amarelo: "text-amarelo",
  vermelho: "text-vermelho",
} as const;

/** `neutro`: o dado é informação, não estado — classe e eixos lidos não pedem nada. */
type CorDoBloco = Significado | "neutro";
const TINTA_DO_BLOCO: Record<CorDoBloco, string> = {
  ...TINTA,
  neutro: "text-tinta bg-painel-2 border-linha",
};

interface Bloco {
  rotulo: string;
  valor: string;
  detalhe: string | null;
  cor: CorDoBloco;
  mono?: boolean;
  /** A certeza de cada letra do `valor`, em %, quando o OCR a mandou (0033). */
  porLetra?: number[] | undefined;
}

interface LeituraDaIa {
  placa: string | null;
  categoria: number | null;
  eixos: number | null;
  blocos: Bloco[];
  /** O que deu errado nas IAs, em frases (03/11): falha do serviço, imagem recusada, sem
   *  garantia de anonimização. Vazio quando tudo correu bem. */
  diagnostico?: string[];
}

/** Confiança de placa em % (o pórtico grava 0–100; a análise, 0–1) → cor. */
function corDaConfianca(pct: number | null): Significado {
  if (pct == null) return "vermelho";
  if (pct >= 90) return "verde";
  if (pct >= 70) return "amarelo";
  return "vermelho";
}

function emPct(v: number | null | undefined): number | null {
  if (v == null) return null;
  return v <= 1 ? v * 100 : v;
}

/**
 * A placa e a certeza dela. **A porcentagem é a da letra mais duvidosa** (ADR-024): uma placa
 * é tão confiável quanto o caractere mais incerto. Por isso uma placa lida certa pode sair
 * com 66% — e a Gisele, em 25/09, leu isso como "o % não bate". Com a certeza de cada letra
 * (0033), o bloco diz **qual** é a duvidosa e a pinta; sem ela (vista antiga, pórtico),
 * fica a porcentagem só.
 */
function blocoDaPlaca(
  placa: string | null,
  pct: number | null,
  onde: string,
  porLetra?: readonly number[] | null,
): Bloco {
  const letras =
    placa && porLetra && porLetra.length === placa.length
      ? porLetra.map((v) => emPct(v) ?? 0)
      : undefined;
  // A porcentagem é a da letra mais fraca, e o texto diz qual é ela — curto, porque o bloco
  // tem duas linhas. As outras duvidosas estão pintadas na própria placa.
  const fraca =
    placa && letras
      ? letras.reduce((m, v, i) => (v < (letras[m] ?? 101) ? i : m), 0)
      : null;
  const certeza =
    placa &&
    letras &&
    fraca !== null &&
    (letras[fraca] ?? 100) < LIMIAR_DA_LETRA
      ? `${percentual(pct)} na letra ${placa[fraca]}`
      : percentual(pct);
  return {
    rotulo: "Placa (OCR)",
    valor: placa ?? "não lida",
    detalhe: placa ? `${certeza} · ${onde}` : onde,
    cor: placa ? corDaConfianca(pct) : "vermelho",
    mono: Boolean(placa),
    porLetra: letras,
  };
}

/** Abaixo disto a letra é pintada: é o limiar do amarelo da placa inteira. */
const LIMIAR_DA_LETRA = 90;

/** Cada letra da placa, e as duvidosas pintadas: vermelho abaixo de 70%, amarelo abaixo de
 *  90% — as mesmas faixas da placa inteira. O título da letra diz a certeza dela. */
function PlacaPorLetra({
  placa,
  porLetra,
}: {
  placa: string;
  porLetra: readonly number[];
}) {
  return (
    <span aria-label={`${placa}, certeza por letra`}>
      {[...placa].map((letra, i) => {
        const v = porLetra[i] ?? 0;
        const cor =
          v < 70
            ? "text-vermelho underline decoration-2 underline-offset-2"
            : v < LIMIAR_DA_LETRA
              ? "text-amarelo underline decoration-dotted underline-offset-2"
              : "text-tinta";
        return (
          <span
            key={i}
            className={cor}
            title={`${letra}: ${percentual(v)}`}
            data-duvidosa={v < LIMIAR_DA_LETRA || undefined}
          >
            {letra}
          </span>
        );
      })}
    </span>
  );
}

function blocoDaClasse(classe: string | null, detalhe: string | null): Bloco {
  return {
    rotulo: "Classe",
    // Passeio ou comercial (ADR-028). O que não tem grupo conhecido aparece como veio.
    valor: grupoDaClasse(classe) ?? classe ?? "não classificada",
    detalhe,
    cor: classe ? "neutro" : "cinza",
  };
}

function blocoDosEixos(eixos: number | null, detalhe: string | null): Bloco {
  return {
    rotulo: "Eixos",
    valor: eixos == null ? "—" : `${eixos} eixos`,
    detalhe,
    cor: eixos == null ? "cinza" : "neutro",
  };
}

/**
 * A isenção, e de onde ela veio. Desde a 0024 o upload tem indício pela **caracterização** —
 * o que está escrito no veículo ("CORPO DE BOMBEIROS", "PREFEITURA") — e o bloco mostra o
 * enquadramento e a palavra lida, para o operador conferir o que a IA viu.
 *
 * Desde a `A-06` (ADR-065) a ordem é do servidor (`detalhe.isencao`): a **lista de isentos**,
 * o **cadastro** e, só então, a caracterização. A fonte vai junto — a lista nunca aparece
 * como base oficial — e a fonte de baixo que discorda vira aviso, em âmbar.
 */
function blocoDaIsencao(detalhe: DetalheDoTransito, porUpload = false): Bloco {
  const evidencias = detalhe.evidencia_isencao ?? [];
  const isencao = detalhe.isencao;
  if (isencao?.origem === "LISTA" || isencao?.origem === "CADASTRO") {
    const onde =
      isencao.origem === "LISTA"
        ? `na lista de isentos${isencao.lista ? `: ${isencao.lista.fundamento}` : ""}`
        : "no cadastro do veículo (base simulada)";
    return {
      rotulo: "Isenção",
      valor: `isento · ${isencao.enquadramento ?? ""}`,
      detalhe: isencao.aviso ? `${onde}. ${isencao.aviso}` : onde,
      cor: isencao.aviso ? "amarelo" : "verde",
    };
  }
  if (isencao?.resultado === "NAO_VERIFICADO" && !detalhe.isento) {
    return {
      rotulo: "Isenção",
      valor: "não verificado",
      detalhe:
        "sem placa lida, a lista de isentos e o cadastro não foram consultados",
      cor: "cinza",
    };
  }
  if (detalhe.isento) {
    return {
      rotulo: "Isenção",
      valor: detalhe.enquadramento_isencao
        ? `isento · ${detalhe.enquadramento_isencao}`
        : "isento",
      detalhe: evidencias.length
        ? `sugerida pela IA: lido "${evidencias.slice(0, 2).join('", "')}"`
        : "sugerida pela IA",
      cor: "verde",
    };
  }
  return {
    rotulo: "Isenção",
    valor: "não isento",
    detalhe: porUpload
      ? "nada escrito no veículo que caracterize isenção"
      : null,
    cor: "cinza",
  };
}

/**
 * O que as IAs disseram, de onde quer que tenha vindo.
 *
 * Trânsito de pórtico: leituras por câmera e a validação automática (`validacoes`).
 * Upload agrupado (0022): as vistas do mesmo veículo, consolidadas em `validacoes`, e a
 * leitura de cada vista em `imagens_de_analise`. Upload antigo, de uma foto só: os
 * resultados do módulo de Análise, pela referência `analise:<id>` — o front compõe, os
 * módulos não se leem.
 */
function leituraDaIa(
  detalhe: DetalheDoTransito,
  analise: ResultadoDaCapacidade[],
  analiseDasVistas: Record<string, ResultadoDaCapacidade[]> = {},
): LeituraDaIa {
  const vistas = detalhe.imagens_de_analise ?? [];
  if (vistas.length > 0) {
    const problemas = problemasDasIas(vistas, analiseDasVistas);
    const semPlaca = falhaDoServico(problemas, "ocr_placa");
    const semEixos = falhaDoServico(problemas, "contagem_eixos");
    const semBlur = falhaDoServico(problemas, "anonimizacao");
    const lidas = vistas.filter((v) => v.placa);
    const melhor = lidas.reduce<(typeof vistas)[number] | null>(
      (m, v) =>
        !m || (v.placa_confianca ?? 0) > (m.placa_confianca ?? 0) ? v : m,
      null,
    );
    const anonimizadas = vistas.filter((v) => v.anonimizada).length;
    const n = vistas.length;
    return {
      placa: melhor?.placa ?? null,
      categoria: detalhe.categoria_sugerida ?? null,
      eixos: detalhe.eixos_detectados,
      diagnostico: problemas.map(fraseDoProblema),
      blocos: [
        // Placa que o serviço nem tentou ler não é placa ilegível: a tela diz o que houve em
        // vez de pedir ao operador que confira à mão uma foto que ninguém leu (03/11).
        !melhor && semPlaca
          ? {
              rotulo: "Placa (OCR)",
              valor: "IA indisponível",
              detalhe: `o serviço de placa não respondeu em ${semPlaca.vistas} de ${semPlaca.de} vistas — não é a foto`,
              cor: "amarelo",
            }
          : blocoDaPlaca(
              melhor?.placa ?? null,
              emPct(melhor?.placa_confianca),
              `lida em ${lidas.length} de ${n} vista${n > 1 ? "s" : ""}`,
              melhor?.placa_confiancas,
            ),
        blocoDaClasse(detalhe.silhueta, votoDaClasse(vistas, detalhe.silhueta)),
        blocoDosEixos(
          detalhe.eixos_detectados,
          [
            detalhe.eixos_detectados == null && semEixos
              ? `o serviço de eixos não respondeu em ${semEixos.vistas} de ${semEixos.de} vistas`
              : motivoDosEixos(vistas, detalhe.eixos_detectados),
            detalhe.categoria_sugerida
              ? `sugere categoria ${detalhe.categoria_sugerida}`
              : null,
          ]
            .filter(Boolean)
            .join(" · ") || null,
        ),
        blocoDaIsencao(detalhe, true),
        {
          rotulo: "Anonimização",
          valor: anonimizadas === n ? "aplicada" : "pendente",
          detalhe:
            anonimizadas < n && semBlur
              ? `o serviço de anonimização não respondeu em ${semBlur.vistas} de ${semBlur.de} vistas — refeita sozinha`
              : `${anonimizadas} de ${n} vista${n > 1 ? "s" : ""} com blur`,
          cor: anonimizadas === n ? "verde" : "amarelo",
        },
        lidas.length < 2
          ? {
              rotulo: "Divergência",
              valor: "sem validação",
              detalhe: "menos de duas leituras",
              cor: "cinza",
            }
          : detalhe.divergencia
            ? {
                rotulo: "Divergência",
                valor: "diverge",
                detalhe: "vistas leram placas diferentes",
                cor: "vermelho",
              }
            : {
                rotulo: "Divergência",
                valor: "converge",
                detalhe: `${lidas.length} vistas concordam`,
                cor: "verde",
              },
      ],
    };
  }

  if (imagemDaAnalise(detalhe)) {
    const por = (c: string) => analise.find((r) => r.capacidade === c);
    const ocr = por("ocr_placa");
    const eixos = por("contagem_eixos");
    const classe = por("classificacao_veiculo");
    const anon = por("anonimizacao");
    const valorOcr = (ocr?.valor ?? {}) as Record<string, unknown>;
    const valorEixos = (eixos?.valor ?? {}) as Record<string, unknown>;
    const valorAnon = (anon?.valor ?? {}) as Record<string, unknown>;
    const placa = (valorOcr.placa as string | null | undefined) ?? null;
    const nEixos = (valorEixos.eixos as number | null | undefined) ?? null;
    const aplicada =
      anon?.desfecho === "SUCESSO" && valorAnon.completa === true;
    const problemas = problemasDasIas([{ imagem_id: "unica" }], {
      unica: analise,
    });
    const semPlaca = falhaDoServico(problemas, "ocr_placa");
    return {
      placa,
      categoria: detalhe.categoria ?? detalhe.categoria_sugerida ?? null,
      eixos: nEixos,
      diagnostico: problemas.map(fraseDoProblema),
      blocos: [
        !placa && semPlaca
          ? {
              rotulo: "Placa (OCR)",
              valor: "IA indisponível",
              detalhe: "o serviço de placa não respondeu — não é a foto",
              cor: "amarelo",
            }
          : blocoDaPlaca(
              placa,
              emPct(valorOcr.confianca as number | undefined),
              ocr ? "imagem enviada" : "ainda não processada",
              Array.isArray(valorOcr.confianca_por_posicao)
                ? (valorOcr.confianca_por_posicao as number[])
                : null,
            ),
        blocoDaClasse(
          detalhe.silhueta ??
            (classe?.desfecho === "SUCESSO"
              ? resumoDaCapacidade(classe).texto
              : null),
          classe ? null : "sem classificação nesta imagem",
        ),
        blocoDosEixos(nEixos, eixos ? resumoDaCapacidade(eixos).texto : null),
        blocoDaIsencao(detalhe, true),
        {
          rotulo: "Anonimização",
          valor: aplicada ? "aplicada" : "pendente",
          detalhe: aplicada
            ? `${String(valorAnon.regioes ?? "—")} região(ões)`
            : anon
              ? "sem garantia — imagem não exibida"
              : "ainda não processada",
          cor: aplicada ? "verde" : "amarelo",
        },
        {
          rotulo: "Divergência",
          valor: "uma imagem",
          detalhe: "sem outra vista para comparar",
          cor: "cinza",
        },
      ],
    };
  }

  const matriz = detalhe.matriz_divergencia as Record<string, string> | null;
  const frontal = detalhe.leituras.find((l) => l.camera === "FRONTAL");
  const lidas = detalhe.leituras.filter((l) => l.placa);
  const placa = frontal?.placa ?? lidas[0]?.placa ?? null;
  const pares = matriz
    ? Object.values(matriz).filter((v) => v === "divergente" || v === "parcial")
        .length
    : 0;
  return {
    placa,
    categoria: detalhe.categoria ?? detalhe.categoria_sugerida ?? null,
    eixos: detalhe.eixos_detectados,
    blocos: [
      blocoDaPlaca(
        placa,
        detalhe.confianca_ponderada ?? frontal?.confianca ?? null,
        `${lidas.length} de ${detalhe.leituras.length} câmeras`,
      ),
      blocoDaClasse(detalhe.silhueta, "silhueta do pórtico"),
      blocoDosEixos(detalhe.eixos_detectados, "câmera de eixo"),
      blocoDaIsencao(detalhe),
      // Imagem de pórtico não sai do cockpit: a anonimização é portão de **saída** (A-04).
      {
        rotulo: "Anonimização",
        valor: "não se aplica",
        detalhe: "imagem de pórtico não sai do cockpit",
        cor: "cinza",
      },
      lidas.length === 0 && detalhe.confianca_ponderada == null
        ? {
            rotulo: "Divergência",
            valor: "sem validação",
            detalhe: "nenhuma câmera leu",
            cor: "cinza",
          }
        : detalhe.divergencia || pares > 0
          ? {
              rotulo: "Divergência",
              valor: "diverge",
              detalhe: `${pares || "câmeras"} par(es) diferem`,
              cor: "vermelho",
            }
          : {
              rotulo: "Divergência",
              valor: "converge",
              detalhe: "frontal, lateral e eixo iguais",
              cor: "verde",
            },
    ],
  };
}

/**
 * O prazo de 24 h deste trânsito, andando. Com folga, uma linha discreta; abaixo de uma hora,
 * um relógio que conta os segundos; vencido, o que acontece em seguida — dito, porque quem
 * assiste precisa saber que a perda é do sistema, não da tela.
 */
function RelogioDoPrazo({ venceEm }: { venceEm: string }) {
  const perto =
    new Date(venceEm).getTime() - agoraDoInquilino().getTime() < 3_700_000;
  const agora = useAgora(1000, perto);
  const { texto, urgencia } = contagem(venceEm, agora);
  if (urgencia === "folga")
    return (
      <span className="mono text-[11px] text-tinta-3">
        prazo 24 h: <b className="text-tinta-2">{texto}</b>
      </span>
    );
  if (urgencia === "vencido")
    return (
      <span
        className="inline-flex items-center gap-1.5"
        title="O motor de prazos marca como não enviável em até um minuto: a cobrança e a autuação deste trânsito estão perdidas."
      >
        <Badge cor="vermelho">24 h vencidas</Badge>
      </span>
    );
  return (
    <span
      role="timer"
      aria-label={`O prazo de 24 horas vence em ${texto}`}
      className={`mono inline-flex items-center gap-1.5 rounded-botao border px-2 py-0.5 text-[13px] font-bold ${
        urgencia === "critico"
          ? "border-vermelho-borda bg-vermelho-fundo text-vermelho"
          : "border-amarelo-borda bg-amarelo-fundo text-amarelo"
      }`}
    >
      <Icone.Relogio />
      vence em {texto}
    </span>
  );
}

function Navegacao({
  posicao,
  total,
  aoNavegar,
}: {
  posicao: number;
  total: number;
  aoNavegar: (passo: -1 | 1) => void;
}) {
  return (
    <span className="flex items-center gap-1">
      <span className="mono text-[11px] text-tinta-3">
        # {posicao} de {total}
      </span>
      <button
        type="button"
        aria-label="Anterior"
        disabled={posicao <= 1}
        onClick={() => aoNavegar(-1)}
        className="rounded-botao border border-linha bg-painel-2 px-1.5 text-tinta-2 disabled:opacity-40"
      >
        ‹
      </button>
      <button
        type="button"
        aria-label="Próximo"
        disabled={posicao >= total}
        onClick={() => aoNavegar(1)}
        className="rounded-botao border border-linha bg-painel-2 px-1.5 text-tinta-2 disabled:opacity-40"
      >
        ›
      </button>
    </span>
  );
}
